import { AggregateStatusEnumLabels } from "@boundless/bl-api-client/partners/models/AggregateStatusEnumLabels"
import { createColumnHelper } from "@tanstack/react-table"
import { ArrowUpDownIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { formatDate } from "@/lib/utils"

import type { FundingRequest } from "@boundless/bl-api-client/partners/models"
import type { Column, Row } from "@tanstack/react-table"

const columnHelper = createColumnHelper<FundingRequest>()

export const fundingRequestColumns = [
  columnHelper.accessor("requestId", {
    header: ({ column }: { column: Column<FundingRequest, unknown> }) => {
      return (
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            className="h-5 w-5 min-w-5"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <ArrowUpDownIcon className="h-4 w-4" />
          </Button>
          <p className="text-center">#</p>
        </div>
      )
    },
    cell: ({ row }: { row: Row<FundingRequest> }) => {
      return <div className="font-small">{row.getValue("requestId")}</div>
    },
  }),
  columnHelper.accessor("createdAt", {
    header: ({ column }: { column: Column<FundingRequest, unknown> }) => {
      return (
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            className="h-5 w-5 min-w-5"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <ArrowUpDownIcon className="h-4 w-4" />
          </Button>
          <p className="text-center">Created</p>
        </div>
      )
    },
    cell: ({ row }: { row: Row<FundingRequest> }) => {
      return (
        <div className="font-small">
          {formatDate(row.getValue("createdAt"))}
        </div>
      )
    },
    filterFn: (
      row: Row<FundingRequest>,
      columnId: string,
      filterValue: unknown,
    ) => {
      const { from, to } = filterValue as {
        from: Date | null
        to: Date | null
      }
      const raw = row.getValue(columnId)
      const rowDate = raw ? new Date(raw as string) : null
      if (!rowDate) return false
      if (from && rowDate < from) return false
      if (to && rowDate > to) return false
      return true
    },
  }),
  columnHelper.accessor("lastModifiedAt", {
    header: ({ column }: { column: Column<FundingRequest, unknown> }) => {
      return (
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            className="h-5 w-5 min-w-5"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <ArrowUpDownIcon className="h-4 w-4" />
          </Button>
          <p className="text-center">Last Modified</p>
        </div>
      )
    },
    cell: ({ row }: { row: Row<FundingRequest> }) => {
      return (
        <div className="font-small">
          {formatDate(row.getValue("lastModifiedAt"))}
        </div>
      )
    },
    filterFn: (
      row: Row<FundingRequest>,
      columnId: string,
      filterValue: unknown,
    ) => {
      const { from, to } = filterValue as {
        from: Date | null
        to: Date | null
      }
      const raw = row.getValue(columnId)
      const rowDate = raw ? new Date(raw as string) : null
      if (!rowDate) return false
      if (from && rowDate < from) return false
      if (to && rowDate > to) return false
      return true
    },
  }),
  columnHelper.accessor("aggregateStatus", {
    header: ({ column }: { column: Column<FundingRequest, unknown> }) => {
      return (
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            className="h-5 w-5 min-w-5"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <ArrowUpDownIcon className="h-4 w-4" />
          </Button>
          <p className="text-center">Status</p>
        </div>
      )
    },
    cell: ({ row }: { row: Row<FundingRequest> }) => {
      return (
        <div className="font-small capitalize">
          {
            AggregateStatusEnumLabels[
              row.getValue(
                "aggregateStatus",
              ) as keyof typeof AggregateStatusEnumLabels
            ]
          }
        </div>
      )
    },
    filterFn: (
      row: Row<FundingRequest>,
      columnId: string,
      filterValue: unknown,
    ) => {
      if (!filterValue) return true
      return row.getValue(columnId) === filterValue
    },
  }),
]
